import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getCoopEvaluationDetail } from '../api/crud';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BarChartIcon from '@mui/icons-material/BarChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HandshakeIcon from '@mui/icons-material/Handshake';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScienceIcon from '@mui/icons-material/Science';
import BoltIcon from '@mui/icons-material/Bolt';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReactApexChart from 'react-apexcharts';
import useMediaQuery from "@mui/material/useMediaQuery";

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

const GradientCard = styled(Card)(({ theme, bgcolor }) => ({
  background: bgcolor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
}));

const SupervisorEvaluationVisualization = ({ reqId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery('(max-width:767px)');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getCoopEvaluationDetail(reqId);
        
        console.log('🔍 Evaluation Data:', result); // Debug
        console.log('📊 Q26.1 Usefulness Score:', result.usefulness?.usefulness_score, typeof result.usefulness?.usefulness_score);
        console.log('📈 Q26.2 Success Level:', result.usefulness?.success_level, typeof result.usefulness?.success_level);
        
        if (!result.success) {
          setError(result.error || 'ไม่พบข้อมูลการประเมิน');
          setData(null);
          return;
        }
        
        setData(result);
      } catch (err) {
        console.error('Error fetching evaluation data:', err);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    if (reqId) {
      fetchData();
    }
  }, [reqId]);

  // Skill Category Grouping - จัดกลุ่มทักษะ 20 มิติเป็น 6 หมวดหลัก
  const getSkillCategories = (dimensions) => {
    if (!dimensions || dimensions.length === 0) return [];

    // Map dimension index to category
    const categoryMapping = {
      'ความรู้และการเรียนรู้': [2, 3, 4], // q3, q4, q5
      'การทำงานและการแก้ปัญหา': [0, 1, 5, 6], // q1, q2, q6, q7
      'การสื่อสารและความสัมพันธ์': [7, 8, 15], // q8, q9, q16 (ย้าย q15 ออก)
      'ความรับผิดชอบและวินัย': [9, 10, 11, 18], // q10, q11, q12, q19
      'ภาวะผู้นำและจริยธรรม': [12, 16, 17, 19], // q13, q17, q18, q20
      'บุคลิกภาพและการปรับตัว': [13, 14] // q14, q15 (เพิ่ม บุคลิก/วางตัว)
    };

    const categories = [];

    Object.entries(categoryMapping).forEach(([categoryName, indices]) => {
      const categoryDimensions = indices.map(i => dimensions[i]).filter(Boolean);
      
      if (categoryDimensions.length > 0) {
        const totalScore = categoryDimensions.reduce((sum, d) => sum + d.score, 0);
        const totalMax = categoryDimensions.reduce((sum, d) => sum + d.max, 0);
        const avgScore = parseFloat((totalScore / categoryDimensions.length).toFixed(2));
        const avgMax = parseFloat((totalMax / categoryDimensions.length).toFixed(2));

        categories.push({
          category: categoryName,
          avgScore: avgScore,
          maxScore: avgMax,
          count: categoryDimensions.length,
          dimensions: categoryDimensions
        });
      }
    });

    return categories;
  };

  // Get top 5 and bottom 5 dimensions
  const getTopBottom = (dimensions) => {
    if (!dimensions || dimensions.length === 0) return { top5: [], bottom5: [] };
    
    const sorted = [...dimensions].sort((a, b) => b.score - a.score);
    const top5 = sorted.slice(0, 5);
    const bottom5 = sorted.slice(-5).reverse();
    
    return { top5, bottom5 };
  };

  // Radar Chart Configuration - แสดงตามหมวดทักษะหลัก
  const getRadarChartOptions = () => {
    if (!data || !data.dimensions) return { series: [], options: {} };

    const skillCategories = getSkillCategories(data.dimensions);

    return {
      series: [{
        name: 'คะแนนเฉลี่ย',
        data: skillCategories.map(c => c.avgScore)
      }],
      options: {
        chart: {
          type: 'radar',
          toolbar: { show: false }
        },
        xaxis: {
          categories: skillCategories.map(c => c.category),
          labels: {
            style: {
              fontSize: isMobile ? '11px' : '13px',
              fontFamily: 'Sarabun, sans-serif',
              fontWeight: 500
            }
          }
        },
        yaxis: {
          show: true,
          min: 0,
          max: 10,
          tickAmount: 5,
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        plotOptions: {
          radar: {
            size: isMobile ? 140 : 180,
            polygons: {
              strokeColors: '#e0e7ff',
              strokeWidth: 1.5,
              fill: {
                colors: ['#f8faff', '#ffffff']
              }
            }
          }
        },
        colors: ['#4f46e5'],
        markers: {
          size: 6,
          colors: ['#4f46e5'],
          strokeColor: '#fff',
          strokeWidth: 2,
          hover: {
            size: 8
          }
        },
        fill: {
          opacity: 0.35
        },
        stroke: {
          width: 3,
          colors: ['#4f46e5']
        },
        legend: {
          show: false
        },
        dataLabels: {
          enabled: true,
          background: {
            enabled: true,
            borderRadius: 4,
            padding: 5,
            opacity: 0.95,
            borderWidth: 1,
            borderColor: '#4f46e5'
          },
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            colors: ['#1e293b']
          }
        },
        tooltip: {
          y: {
            formatter: function(val, { seriesIndex, dataPointIndex }) {
              const category = skillCategories[dataPointIndex];
              return `${val.toFixed(2)}/${category.maxScore.toFixed(2)} (${category.count} มิติ)`;
            }
          }
        }
      }
    };
  };

  // Bar Chart for Bottom 5
  const getBottomBarChartOptions = () => {
    if (!data || !data.dimensions) return { series: [], options: {} };
    
    const { bottom5 } = getTopBottom(data.dimensions);

    return {
      series: [{
        name: 'คะแนน',
        data: bottom5.map(d => d.score)
      }],
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "/10";
          },
          offsetX: 30,
          style: {
            fontSize: '12px',
            colors: ['#304758']
          }
        },
        xaxis: {
          categories: bottom5.map(d => d.dimension),
          max: 10,
          labels: {
            style: {
              fontSize: '11px',
              fontFamily: 'Sarabun, sans-serif'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '11px',
              fontFamily: 'Sarabun, sans-serif'
            }
          }
        },
        colors: ['#f44336'],
        grid: {
          borderColor: '#f1f1f1'
        }
      }
    };
  };

  // Overall Score Color
  const getOverallScoreColor = (score) => {
    if (score >= 4.5) return '#4caf50';
    if (score >= 4) return '#2196f3';
    if (score >= 3) return '#ff9800';
    if (score >= 2) return '#f44336';
    return '#9e9e9e';
  };

  // Soft tone background for score chips (0-10 scale)
  const getScoreChipStyle = (score) => {
    const s = Number(score) || 0;
    if (s >= 9) {
      return { bgcolor: '#e8f5e9', color: '#2e7d32', borderColor: '#a5d6a7' };
    } else if (s >= 8) {
      return { bgcolor: '#e3f2fd', color: '#1565c0', borderColor: '#90caf9' };
    } else if (s >= 7) {
      return { bgcolor: '#fff8e1', color: '#ef6c00', borderColor: '#ffe082' };
    } else if (s >= 6) {
      return { bgcolor: '#fff3e0', color: '#f57c00', borderColor: '#ffcc80' };
    }
    return { bgcolor: '#ffebee', color: '#c62828', borderColor: '#ef9a9a' };
  };

  // Parse future interest response
  const getFutureInterestStatus = (text) => {
    if (!text) return { status: 'unknown', label: 'ไม่ระบุ', color: '#9e9e9e', icon: HelpIcon };
    
    const lowerText = text.toLowerCase().trim();
    
    // รับ
    if (lowerText === 'รับ' || lowerText.includes('สนใจรับ') || lowerText.includes('ยินดีรับ') || lowerText.includes('พร้อมรับ')) {
      return { status: 'accept', label: 'รับ', color: '#4caf50', icon: CheckCircleIcon };
    }
    
    // ไม่รับ
    if (lowerText === 'ไม่รับ' || lowerText.includes('ไม่สนใจ') || lowerText.includes('ไม่สามารถรับ')) {
      return { status: 'reject', label: 'ไม่รับ', color: '#f44336', icon: CancelIcon };
    }
    
    // ไม่แน่ใจ
    if (lowerText === 'ไม่แน่ใจ' || lowerText.includes('อาจจะ') || lowerText.includes('พิจารณา') || lowerText.includes('ดูสถานการณ์')) {
      return { status: 'maybe', label: 'ไม่แน่ใจ', color: '#ff9800', icon: HelpIcon };
    }
    
    // Default - แสดงข้อความเต็ม
    return { status: 'text', label: text, color: '#2196f3', icon: HandshakeIcon };
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // ฟังก์ชันสำหรับแยกรหัสนิสิต 8 ตัวแรกจาก req_id (ใช้เหมือนหน้า InternshipEvaluation)
  const getStudentId = (reqId) => {
    return reqId ? reqId.toString().substring(0, 8) : '';
  };

  // ฟังก์ชันสำหรับสร้าง URL รูปนิสิต
  const getStudentImageUrl = (reqId) => {
    const studentId = getStudentId(reqId);
    return studentId
      ? `https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${studentId}`
      : `${process.env.PUBLIC_URL}/assets/images/logo.png`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>กำลังโหลดข้อมูล...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 'auto', maxWidth: 600 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mx: 'auto', maxWidth: 600 }}>
        ไม่พบข้อมูลการประเมินสำหรับ REQ ID: {reqId}
      </Alert>
    );
  }

  const { top5, bottom5 } = getTopBottom(data.dimensions);
  const skillCategories = getSkillCategories(data.dimensions);
  const radarChart = getRadarChartOptions();
  const bottomBarChart = getBottomBarChartOptions();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        mb={4}
        sx={{
          bgcolor: 'success.light',
          borderRadius: 2,
          p: 3,
          color: 'common.white',
          boxShadow: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.6rem', md: '2rem' },
                mb: 0.5,
                color: 'common.white',
              }}
            >
              ผลประเมินผลการปฏิบัติสหกิจศึกษา
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.95, color: 'common.white' }}>
              คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`REQ ID: ${data.header.req_id}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'common.white',
                fontWeight: 'bold',
              }}
            />
            <Tooltip title="พิมพ์รายงาน">
              <IconButton
                onClick={handlePrint}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
                className="no-print"
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Student & Evaluator Info */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={7}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                ข้อมูลนิสิต
              </Typography>
              <Box sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      ชื่อ-นามสกุล:
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      {data.header.student.name}
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      สาขาวิชา:
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      {data.header.student.program}
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                  >
                    <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      สถานประกอบการ:
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      {data.header.student.company}
                    </Box>
                  </Typography>
                  {data.header.student.department && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                    >
                      <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        แผนก/ฝ่าย:
                      </Box>
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {data.header.student.department}
                      </Box>
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flexShrink: 0, ml: { xs: 0, sm: 1 } }}>
                  <img
                    src={getStudentImageUrl(data.header.req_id)}
                    alt="รูปนิสิต"
                    style={{
                      width: 120,
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 12,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    }}
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL}/assets/images/logo.png`;
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main" fontWeight="bold">
                ผู้ประเมิน
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                >
                  <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    ชื่อ:
                  </Box>
                  <Box component="span" sx={{ ml: 0.5 }}>
                    {data.header.evaluator.name}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                >
                  <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    ตำแหน่ง:
                  </Box>
                  <Box component="span" sx={{ ml: 0.5 }}>
                    {data.header.evaluator.position}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                >
                  <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    อีเมล:
                  </Box>
                  <Box component="span" sx={{ ml: 0.5 }}>
                    {data.header.evaluator.email}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.5 }}
                >
                  <Box component="span" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    เบอร์โทร:
                  </Box>
                  <Box component="span" sx={{ ml: 0.5 }}>
                    {data.header.evaluator.phone}
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  วันที่ประเมิน: {data.header.submitted_at}
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Summary Score Cards & Radar Chart */}
      <Grid container spacing={3} mb={4}>
        {/* Left Column - Summary Cards */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <GradientCard bgcolor="linear-gradient(135deg, #a8b5ff 0%, #c9b3ff 100%)">
                <CardContent sx={{ py: 3, px: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5, color: '#4a5568' }}>
                        {data.scores.total}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85, color: '#4a5568' }}>
                        คะแนนรวม (เต็ม {data.scores.max})
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5, color: '#4a5568' }}>
                        {data.scores.percent}%
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85, color: '#4a5568' }}>
                        เปอร์เซ็นต์
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ 
                    width: '100%', 
                    height: 12, 
                    bgcolor: 'rgba(102, 126, 234, 0.2)', 
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      width: `${data.scores.percent}%`, 
                      height: '100%', 
                      bgcolor: '#667eea',
                      borderRadius: 2,
                      transition: 'width 1s ease-in-out',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
                    }} />
                  </Box>
                </CardContent>
              </GradientCard>
            </Grid>

            <Grid item xs={12}>
              <GradientCard bgcolor="linear-gradient(135deg, #81c995 0%, #8fd19e 100%)">
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ lineHeight: 1, color: '#2d5f3f' }}>
                      {data.scores.overall_score}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.8, mt: 0.5, color: '#2d5f3f' }}>
                      /10
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3, mb: 1.5 }}>
                    {[...Array(5)].map((_, i) => {
                      const starValue = data.scores.overall_score / 2;
                      const isFull = i < Math.floor(starValue);
                      const isHalf = i === Math.floor(starValue) && starValue % 1 >= 0.5;
                  const isEmpty = !isFull && !isHalf;
                  
                  return (
                    <Box
                      key={i}
                      component="span"
                      sx={{
                        fontSize: '1.5rem',
                        color: '#fbbf24',
                        position: 'relative',
                        display: 'inline-block',
                      }}
                    >
                      {isHalf ? (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box component="span" sx={{ opacity: 0.25, color: '#e5e7eb' }}>★</Box>
                          <Box
                            component="span"
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: '50%',
                              overflow: 'hidden',
                              opacity: 1,
                              color: '#fbbf24',
                            }}
                          >
                            ★
                          </Box>
                        </Box>
                      ) : (
                        <Box component="span" sx={{ opacity: isFull ? 1 : 0.25, color: isFull ? '#fbbf24' : '#e5e7eb' }}>
                          ★
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, color: '#2d5f3f' }}>
                คุณภาพนิสิตโดยรวม
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5, fontSize: '0.7rem', color: '#2d5f3f' }}>
                สรุปความคิดเห็นต่อคุณภาพนิสิต
              </Typography>
                </CardContent>
              </GradientCard>
            </Grid>

            {/* Summary Insights Card */}
            <Grid item xs={12}>
              <StyledCard>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BarChartIcon color="primary" />
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      สรุปผลการประเมิน
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {/* จุดแข็งโดดเด่น */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 2, borderLeft: '4px solid #2196f3', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmojiEventsIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              จุดแข็งโดดเด่น
                            </Typography>
                            {skillCategories.length > 0 ? (
                              <Typography variant="body1" fontWeight="bold" color="primary">
                                {(() => {
                                  const maxCat = skillCategories.reduce((max, cat) => 
                                    (cat.avgScore || 0) > (max.avgScore || 0) ? cat : max, skillCategories[0]
                                  );
                                  return maxCat.category || '-';
                                })()}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">ไม่มีข้อมูล</Typography>
                            )}
                          </Box>
                        </Box>
                        {skillCategories.length > 0 && (
                          <Tooltip title={`${(() => {
                            const maxCat = skillCategories.reduce((max, cat) => 
                              (cat.avgScore || 0) > (max.avgScore || 0) ? cat : max, skillCategories[0]
                            );
                            return (maxCat.avgScore || 0).toFixed(2);
                          })()}/10 คะแนน`} arrow placement="top">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              {(() => {
                                const maxCat = skillCategories.reduce((max, cat) => 
                                  (cat.avgScore || 0) > (max.avgScore || 0) ? cat : max, skillCategories[0]
                                );
                                const score = maxCat.avgScore || 0;
                                const starScore = score / 2;
                                const fullStars = Math.floor(starScore);
                                const hasHalfStar = (starScore % 1) >= 0.5;
                                
                                return (
                                  <>
                                    {[...Array(5)].map((_, i) => {
                                      if (i < fullStars) {
                                        return <span key={i} style={{ color: '#fbbf24', fontSize: '24px' }}>★</span>;
                                      } else if (i === fullStars && hasHalfStar) {
                                        return (
                                          <span key={i} style={{ position: 'relative', fontSize: '24px' }}>
                                            <span style={{ color: '#e5e7eb' }}>★</span>
                                            <span style={{ position: 'absolute', left: 0, overflow: 'hidden', width: '50%', color: '#fbbf24' }}>★</span>
                                          </span>
                                        );
                                      } else {
                                        return <span key={i} style={{ color: '#e5e7eb', fontSize: '24px' }}>★</span>;
                                      }
                                    })}
                                  </>
                                );
                              })()}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>

                    {/* จุดที่ควรพัฒนา */}
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: '#fff4e6', borderRadius: 2, borderLeft: '4px solid #ff9800', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LightbulbIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              จุดที่ควรพัฒนา
                            </Typography>
                            {skillCategories.length > 0 ? (
                              <Typography variant="body1" fontWeight="bold" color="warning.main">
                                {(() => {
                                  const minCat = skillCategories.reduce((min, cat) => 
                                    (cat.avgScore || 0) < (min.avgScore || 0) ? cat : min, skillCategories[0]
                                  );
                                  return minCat.category || '-';
                                })()}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">ไม่มีข้อมูล</Typography>
                            )}
                          </Box>
                        </Box>
                        {skillCategories.length > 0 && (
                          <Tooltip title={`${(() => {
                            const minCat = skillCategories.reduce((min, cat) => 
                              (cat.avgScore || 0) < (min.avgScore || 0) ? cat : min, skillCategories[0]
                            );
                            return (minCat.avgScore || 0).toFixed(2);
                          })()}/10 คะแนน`} arrow placement="top">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              {(() => {
                                const minCat = skillCategories.reduce((min, cat) => 
                                  (cat.avgScore || 0) < (min.avgScore || 0) ? cat : min, skillCategories[0]
                                );
                                const score = minCat.avgScore || 0;
                                const starScore = score / 2;
                                const fullStars = Math.floor(starScore);
                                const hasHalfStar = (starScore % 1) >= 0.5;
                                
                                return (
                                  <>
                                    {[...Array(5)].map((_, i) => {
                                      if (i < fullStars) {
                                        return <span key={i} style={{ color: '#fbbf24', fontSize: '24px' }}>★</span>;
                                      } else if (i === fullStars && hasHalfStar) {
                                        return (
                                          <span key={i} style={{ position: 'relative', fontSize: '24px' }}>
                                            <span style={{ color: '#e5e7eb' }}>★</span>
                                            <span style={{ position: 'absolute', left: 0, overflow: 'hidden', width: '50%', color: '#fbbf24' }}>★</span>
                                          </span>
                                        );
                                      } else {
                                        return <span key={i} style={{ color: '#e5e7eb', fontSize: '24px' }}>★</span>;
                                      }
                                    })}
                                  </>
                                );
                              })()}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Radar Chart */}
        <Grid item xs={12} lg={7}>
          <StyledCard sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                ผลการประเมินตามหมวดทักษะ
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                แสดงคะแนนเฉลี่ยในแต่ละหมวดทักษะ (คะแนนเต็ม 10)
              </Typography>
              <Box sx={{ height: { xs: 400, md: 500 }, mt: 2 }}>
                <ReactApexChart
                  options={radarChart.options}
                  series={radarChart.series}
                  type="radar"
                  height="100%"
                />
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Top 5 & Bottom 3 Section */}
      <Grid container spacing={3} mb={4}>
        {/* จุดเด่น - Top 5 */}
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingUpIcon color="success" />
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  จุดเด่น - Top 5
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>หัวข้อการประเมิน</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>ระดับดาว</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top5.map((item, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8fff8' } }}>
                        <TableCell>{(item.dimension || '').toString().replace(/^\s*\d+\s*/, '')}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={`${Number(item.score).toFixed(2)}/${item.max} คะแนน`} arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                              {[...Array(5)].map((_, i) => {
                                const starScore = (Number(item.score) || 0) / 2; // convert 10 -> 5
                                const full = i < Math.floor(starScore);
                                const half = i === Math.floor(starScore) && starScore % 1 >= 0.5;
                                return (
                                  <Box key={i} component="span" sx={{ position: 'relative', fontSize: '1.1rem' }}>
                                    {half ? (
                                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box component="span" sx={{ color: '#e5e7eb' }}>★</Box>
                                        <Box component="span" sx={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: '#fbbf24' }}>★</Box>
                                      </Box>
                                    ) : (
                                      <Box component="span" sx={{ color: full ? '#fbbf24' : '#e5e7eb', opacity: full ? 1 : 1 }}>★</Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* จุดที่ควรพัฒนา - Bottom 3 */}
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrendingDownIcon color="error" />
                <Typography variant="h6" color="error" fontWeight="bold">
                  จุดที่ควรพัฒนา - Bottom 3
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>หัวข้อการประเมิน</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>ระดับดาว</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bottom5.slice(0, 3).map((item, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fff8f8' } }}>
                        <TableCell>{(item.dimension || '').toString().replace(/^\s*\d+\s*/, '')}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={`${Number(item.score).toFixed(2)}/${item.max} คะแนน`} arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                              {[...Array(5)].map((_, i) => {
                                const starScore = (Number(item.score) || 0) / 2; // convert 10 -> 5
                                const full = i < Math.floor(starScore);
                                const half = i === Math.floor(starScore) && starScore % 1 >= 0.5;
                                return (
                                  <Box key={i} component="span" sx={{ position: 'relative', fontSize: '1.1rem' }}>
                                    {half ? (
                                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box component="span" sx={{ color: '#e5e7eb' }}>★</Box>
                                        <Box component="span" sx={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: '#fbbf24' }}>★</Box>
                                      </Box>
                                    ) : (
                                      <Box component="span" sx={{ color: full ? '#fbbf24' : '#e5e7eb', opacity: full ? 1 : 1 }}>★</Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Skill Categories Detail Table */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <ListAltIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  รายละเอียดหมวดทักษะหลัก
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>หมวดทักษะหลัก</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', width: '15%' }}>คะแนนเฉลี่ย</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '55%' }}>รายการตัวชี้วัดที่ประเมิน (คะแนนเต็ม 10)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skillCategories.map((category, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {category.category}
                            </Typography>
                            <Tooltip title={`จำนวนมิติที่ประเมิน: ${category.count}`} arrow>
                              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`${category.avgScore.toFixed(2)}/${category.maxScore.toFixed(2)} คะแนน`} arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                              {[...Array(5)].map((_, i) => {
                                const starScore = (category.avgScore || 0) / 2;
                                const full = i < Math.floor(starScore);
                                const half = i === Math.floor(starScore) && starScore % 1 >= 0.5;
                                return (
                                  <Box key={i} component="span" sx={{ position: 'relative', fontSize: '1.1rem' }}>
                                    {half ? (
                                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box component="span" sx={{ color: '#e5e7eb' }}>★</Box>
                                        <Box component="span" sx={{ position: 'absolute', left: 0, top: 0, width: '50%', overflow: 'hidden', color: '#fbbf24' }}>★</Box>
                                      </Box>
                                    ) : (
                                      <Box component="span" sx={{ color: full ? '#fbbf24' : '#e5e7eb' }}>★</Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {category.dimensions.map((dim, dimIndex) => {
                              const style = getScoreChipStyle(dim.score);
                              return (
                                <Chip
                                  key={dimIndex}
                                  label={`${(dim.dimension || '').replace(/^\s*\d+\s*/, '')}: ${dim.score}`}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    fontSize: '0.75rem',
                                    ...style,
                                    border: `1px solid ${style.borderColor}`,
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Comments Section */}
      <Box mb={4}>
        <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon />
          ข้อคิดเห็นและข้อเสนอแนะจากพี่เลี้ยง
        </Typography>
        <Grid container spacing={2}>
          {data.comments.strength && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                borderLeft: '4px solid #4caf50',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmojiEventsIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="#2e7d32">
                      จุดเด่น / จุดแข็ง
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    bgcolor: '#f1f8f4', 
                    p: 1.5, 
                    borderRadius: 1,
                    lineHeight: 1.7,
                    color: '#1b5e20',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {data.comments.strength}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {data.comments.improve && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                borderLeft: '4px solid #ff9800',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LightbulbIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="#e65100">
                      ข้อเสนอแนะ / จุดที่ควรพัฒนา
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    bgcolor: '#fff8f0', 
                    p: 1.5, 
                    borderRadius: 1,
                    lineHeight: 1.7,
                    color: '#e65100',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {data.comments.improve}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {data.comments.future_interest && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%',
                borderLeft: '4px solid #2196f3',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <HandshakeIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="#1565c0">
                      ความสนใจรับนิสิตในอนาคต
                    </Typography>
                  </Box>
                  {(() => {
                    const interest = getFutureInterestStatus(data.comments.future_interest);
                    const IconComponent = interest.icon;
                    
                    if (interest.status === 'text') {
                      // แสดงข้อความเต็ม
                      return (
                        <Typography variant="body2" sx={{ 
                          bgcolor: '#f0f7ff', 
                          p: 1.5, 
                          borderRadius: 1,
                          lineHeight: 1.7,
                          color: '#0d47a1',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {data.comments.future_interest}
                        </Typography>
                      );
                    } else {
                      // แสดงแบบ visual indicator แบบกะทัดรัด
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          bgcolor: interest.status === 'accept' ? '#e8f5e9' : 
                                   interest.status === 'reject' ? '#ffebee' : 
                                   interest.status === 'maybe' ? '#fff8e1' : '#f5f5f5',
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${interest.color}`
                        }}>
                          <IconComponent sx={{ 
                            fontSize: 28, 
                            color: interest.color 
                          }} />
                          <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: interest.color }}>
                              {interest.label}
                            </Typography>
                            {interest.status === 'maybe' && (
                              <Typography variant="caption" color="text.secondary">
                                อยู่ระหว่างพิจารณา
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }
                  })()}
                </CardContent>
              </Card>
            </Grid>
          )}

          {data.comments.other && (
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                borderLeft: '4px solid #9c27b0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <DescriptionIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="#7b1fa2">
                      ความคิดเห็นเพิ่มเติม
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    bgcolor: '#f8f4f9', 
                    p: 1.5, 
                    borderRadius: 1,
                    lineHeight: 1.7,
                    color: '#6a1b9a',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {data.comments.other}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Organization Feedback Section */}
      {data.usefulness && (
        <Box mt={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 3 }}>
                📊 การประเมินจากหน่วยงาน
              </Typography>

              {/* Q25 - การประเมินหลักสูตรและการดำเนินงาน */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom color="secondary" fontWeight="bold" sx={{ mb: 2 }}>
                  การประเมินหลักสูตรและการดำเนินงานสหกิจศึกษา
                </Typography>

                {/* Summary Cards */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          สหกิจศึกษามีประโยชน์ต่อองค์กรของท่าน
                        </Typography>
                        {(() => {
                          const avgScore = ((data.orgFeedback['25_1_1'] + data.orgFeedback['25_1_2'] + data.orgFeedback['25_1_3']) / 3);
                          const starScore = avgScore / 2;
                          const fullStars = Math.floor(starScore);
                          const hasHalfStar = (starScore % 1) >= 0.5;
                          
                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1 }}>
                                {[...Array(5)].map((_, i) => {
                                  if (i < fullStars) {
                                    return <span key={i} style={{ color: '#fbbf24', fontSize: '28px' }}>★</span>;
                                  } else if (i === fullStars && hasHalfStar) {
                                    return (
                                      <span key={i} style={{ position: 'relative', fontSize: '28px' }}>
                                        <span style={{ color: '#e5e7eb' }}>★</span>
                                        <span style={{ position: 'absolute', left: 0, overflow: 'hidden', width: '50%', color: '#fbbf24' }}>★</span>
                                      </span>
                                    );
                                  } else {
                                    return <span key={i} style={{ color: '#e5e7eb', fontSize: '28px' }}>★</span>;
                                  }
                                })}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {avgScore.toFixed(2)}/10 (เฉลี่ย 3 หัวข้อ)
                              </Typography>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#f3e5f5', borderLeft: '4px solid #9c27b0' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          ด้านหลักสูตร
                        </Typography>
                        {(() => {
                          const avgScore = ((data.orgFeedback['25_2_1'] + data.orgFeedback['25_2_2']) / 2);
                          const starScore = avgScore / 2;
                          const fullStars = Math.floor(starScore);
                          const hasHalfStar = (starScore % 1) >= 0.5;
                          
                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1 }}>
                                {[...Array(5)].map((_, i) => {
                                  if (i < fullStars) {
                                    return <span key={i} style={{ color: '#fbbf24', fontSize: '28px' }}>★</span>;
                                  } else if (i === fullStars && hasHalfStar) {
                                    return (
                                      <span key={i} style={{ position: 'relative', fontSize: '28px' }}>
                                        <span style={{ color: '#e5e7eb' }}>★</span>
                                        <span style={{ position: 'absolute', left: 0, overflow: 'hidden', width: '50%', color: '#fbbf24' }}>★</span>
                                      </span>
                                    );
                                  } else {
                                    return <span key={i} style={{ color: '#e5e7eb', fontSize: '28px' }}>★</span>;
                                  }
                                })}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {avgScore.toFixed(2)}/10 (เฉลี่ย 2 หัวข้อ)
                              </Typography>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          กระบวนการดำเนินงานสหกิจศึกษา
                        </Typography>
                        {(() => {
                          const avgScore = ((data.orgFeedback['25_3_1'] + data.orgFeedback['25_3_2'] + data.orgFeedback['25_3_3'] + 
                             data.orgFeedback['25_3_4'] + data.orgFeedback['25_3_5'] + data.orgFeedback['25_3_6'] + 
                             data.orgFeedback['25_3_7'] + data.orgFeedback['25_3_8']) / 8);
                          const starScore = avgScore / 2;
                          const fullStars = Math.floor(starScore);
                          const hasHalfStar = (starScore % 1) >= 0.5;
                          
                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1 }}>
                                {[...Array(5)].map((_, i) => {
                                  if (i < fullStars) {
                                    return <span key={i} style={{ color: '#fbbf24', fontSize: '28px' }}>★</span>;
                                  } else if (i === fullStars && hasHalfStar) {
                                    return (
                                      <span key={i} style={{ position: 'relative', fontSize: '28px' }}>
                                        <span style={{ color: '#e5e7eb' }}>★</span>
                                        <span style={{ position: 'absolute', left: 0, overflow: 'hidden', width: '50%', color: '#fbbf24' }}>★</span>
                                      </span>
                                    );
                                  } else {
                                    return <span key={i} style={{ color: '#e5e7eb', fontSize: '28px' }}>★</span>;
                                  }
                                })}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {avgScore.toFixed(2)}/10 (เฉลี่ย 8 หัวข้อ)
                              </Typography>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                {/* ด้านทั่วไป */}
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    สหกิจศึกษามีประโยชน์ต่อองค์กรของท่าน
                  </Typography>
                  <TableContainer component={Box} sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '70%' }}>รายการประเมิน</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '30%' }}>คะแนน (เต็ม 10)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>1. ความพร้อมของนิสิตในการออกไปปฏิบัติงานสหกิจศึกษา</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_1_1']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_1_1']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_1_1']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>2. ความรับผิดชอบต่อหน้าที่และการปฏิบัติงานของนิสิต</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_1_2']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_1_2']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_1_2']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>3. ความเหมาะสมของระยะเวลาที่นิสิตออกไปปฏิบัติงานสหกิจศึกษา</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_1_3']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_1_3']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_1_3']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* ด้านหลักสูตร */}
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    ด้านหลักสูตร
                  </Typography>
                  <TableContainer component={Box} sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '70%' }}>รายการประเมิน</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '30%' }}>คะแนน (เต็ม 10)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>1. การกำหนดให้สหกิจศึกษาเป็นหลักสูตรบังคับสำหรับนิสิตทุกคน</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_2_1']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_2_1']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_2_1']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>2. นิสิตต้องไปปฏิบัติงานสหกิจศึกษาอย่างน้อย 1 ครั้ง อย่างน้อย 4 เดือนขึ้นไป</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_2_2']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_2_2']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_2_2']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* ด้านการดำเนินงาน */}
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    กระบวนการดำเนินงานสหกิจศึกษา
                  </Typography>
                  <TableContainer component={Box} sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '70%' }}>รายการประเมิน</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '30%' }}>คะแนน (เต็ม 10)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>1. มีหน่วยงานกลางในการติดต่อประสานงาน</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_1']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_1']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_1']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>2. จัดส่งแบบสำรวจความต้องการรับนักศึกษาและแบบเสนองานสหกิจศึกษาให้สถานประกอบการเสนองานอย่างน้อย 1 ภาคการศึกษา</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_2']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_2']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_2']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>3. ความเหมาะสมของช่องทางการคัดเลือกนิสิตสหกิจศึกษา</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_3']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_3']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_3']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>4. ความเหมาะสมของระยะเวลาการส่งจดหมายสมัครงานนิสิตสหกิจศึกษาให้สถานประกอบการคัดเลือก</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_4']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_4']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_4']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>5. มีการนัดหมายการเข้านิเทศงานของอาจารย์ล่วงหน้า และคณาจารย์นิเทศเข้านิเทศงานตามระยะเวลาที่นัดหมาย</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_5']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_5']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_5']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>6. การนิเทศงานของอาจารย์มีประโยชน์ต่อการปฏิบัติงานของนักศึกษาและสถานประกอบการ</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_6']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_6']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_6']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>7. จำนวนครั้งที่อาจารย์มานิเทศมีความเพียงพอ</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_7']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_7']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_7']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#fff' }}>
                          <TableCell>8. คุณภาพการนิเทศงานสหกิจศึกษาของอาจารย์</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${data.orgFeedback['25_3_8']}`} 
                              size="small" 
                              sx={{
                                ...getScoreChipStyle(data.orgFeedback['25_3_8']),
                                border: `1px solid ${getScoreChipStyle(data.orgFeedback['25_3_8']).borderColor}`,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              {/* Q26 - การใช้ประโยชน์ */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom color="secondary" fontWeight="bold" sx={{ mb: 2 }}>
                การนำผลงานไปใช้ประโยชน์
              </Typography>

              {/* Project Title - หัวข้อโครงงาน */}
              {data.proposal && data.proposal.title && (
                <Card sx={{ 
                  mb: 2,
                  borderLeft: '4px solid #9c27b0',
                  bgcolor: '#fafafa'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                      <Box sx={{ 
                        bgcolor: '#9c27b0',
                        color: 'white',
                        p: 1,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 40,
                        height: 40
                      }}>
                        <AssignmentIcon />
                      </Box>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                            หัวข้อโครงงานสหกิจศึกษา
                          </Typography>
                          {data.proposal.total_score !== null && data.proposal.max_score !== null && (
                            <Tooltip title="คลิกเพื่อดูรายละเอียดการประเมินเล่มรายงาน" placement="top" arrow>
                              <Chip 
                                component={Link}
                                to={`/coop-report-evaluation?req_id=${data.header.req_id}`}
                                target="_blank"
                                label={`เล่มรายงาน ${data.proposal.total_score} / ${data.proposal.max_score} คะแนน`}
                                size="small"
                                clickable
                                sx={{
                                  bgcolor: '#e1bee7',
                                  color: '#4a148c',
                                  fontWeight: 'bold',
                                  border: '1px solid #ce93d8',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    bgcolor: '#ce93d8',
                                    borderColor: '#ba68c8'
                                  }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body1" sx={{ 
                          mt: 0.5,
                          lineHeight: 1.6,
                          color: '#4a148c',
                          fontWeight: 'medium'
                        }}>
                          {data.proposal.title}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
              
              <Grid container spacing={2}>
                {/* Q26.1 - การใช้ประโยชน์ */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    height: '100%',
                    borderLeft: '4px solid #2196f3',
                    bgcolor: '#fafafa'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
                        สถานประกอบการนำผลการปฏิบัติงาน/โครงงานไปใช้ประโยชน์หรือไม่
                      </Typography>
                      <Box sx={{ mt: 1.5 }}>
                        {data.usefulness.usefulness_score === 'ใช้' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                            <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'medium' }}>
                              ใช้
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.usefulness_score === 'ยังไม่ใช้ประโยชน์ ณ เวลานี้ แต่คาดว่าจะนำไปใช้ประโยชน์ในอนาคต' && (
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <AccessTimeIcon sx={{ color: '#ff9800', fontSize: 28, mt: 0.5 }} />
                            <Typography variant="body1" sx={{ color: '#f57c00', fontWeight: 'medium' }}>
                              ยังไม่ใช้ประโยชน์ ณ เวลานี้ แต่คาดว่าจะนำไปใช้ประโยชน์ในอนาคต
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.usefulness_score === 'ผลการปฏิบัติงาน/โครงงานที่นักศึกษาสหกิจศึกษาทำไม่มีประโยชน์ต่อสถานประกอบการ' && (
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <CancelIcon sx={{ color: '#f44336', fontSize: 28, mt: 0.5 }} />
                            <Typography variant="body1" sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
                              โครงงานที่นักศึกษาทำไม่มีประโยชน์ต่อสถานประกอบการ
                            </Typography>
                          </Box>
                        )}
                        {!data.usefulness.usefulness_score && (
                          <Typography variant="body2" color="text.secondary">
                            ไม่ระบุ
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Q26.2 - ระดับความสำเร็จของโครงงาน */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    height: '100%',
                    borderLeft: '4px solid #4caf50',
                    bgcolor: '#fafafa'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
                        ระดับความสำเร็จของโครงงาน
                      </Typography>
                      <Box sx={{ mt: 1.5 }}>
                        {data.usefulness.success_level === 'อยู่ในระดับออกแบบ (Design)' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <DesignServicesIcon sx={{ color: '#2196f3', fontSize: 28 }} />
                            <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 'medium' }}>
                              อยู่ในระดับออกแบบ (Design)
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'อยู่ในระดับจำลอง ยังไม่สามารถสร้างขึ้นใช้งานจริง (Simulation)' && (
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <ScienceIcon sx={{ color: '#9c27b0', fontSize: 28, mt: 0.5 }} />
                            <Typography variant="body1" sx={{ color: '#7b1fa2', fontWeight: 'medium' }}>
                              อยู่ในระดับจำลอง ยังไม่สามารถสร้างขึ้นใช้งานจริง (Simulation)
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'อยู่ในระดับทดลองใช้ (Trial)' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <BoltIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                            <Typography variant="body1" sx={{ color: '#f57c00', fontWeight: 'medium' }}>
                              อยู่ในระดับทดลองใช้ (Trial)
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'อยู่ในระดับใช้งานจริงแล้ว แต่อาจจะไม่ใช้ต่อ (In active use but may not be continued)' && (
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <RocketLaunchIcon sx={{ color: '#00bcd4', fontSize: 28, mt: 0.5 }} />
                            <Typography variant="body1" sx={{ color: '#0097a7', fontWeight: 'medium' }}>
                              อยู่ในระดับใช้งานจริงแล้ว แต่อาจจะไม่ใช้ต่อ
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'อยู่ในระดับใช้จริงแล้ว และจะใช้ต่อไปเรื่อยๆ (In active use and ongoing)' && (
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <AutoAwesomeIcon sx={{ color: '#4caf50', fontSize: 28, mt: 0.5 }} />
                            <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'medium' }}>
                              อยู่ในระดับใช้จริงแล้ว และจะใช้ต่อไปเรื่อยๆ
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'อยู่ในระดับนวัตกรรม (Innovation)' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <LightbulbIcon sx={{ color: '#ffc107', fontSize: 28 }} />
                            <Typography variant="body1" sx={{ color: '#f57f17', fontWeight: 'medium' }}>
                              อยู่ในระดับนวัตกรรม (Innovation)
                            </Typography>
                          </Box>
                        )}
                        {data.usefulness.success_level === 'ไม่ใช้ประโยชน์ของโครงงาน' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CloseIcon sx={{ color: '#f44336', fontSize: 28 }} />
                            <Typography variant="body1" sx={{ color: '#d32f2f', fontWeight: 'medium' }}>
                              ไม่ใช้ประโยชน์ของโครงงาน
                            </Typography>
                          </Box>
                        )}
                        {!data.usefulness.success_level && (
                          <Typography variant="body2" color="text.secondary">
                            ไม่ระบุ
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* เหตุผลที่ไม่ได้ประโยชน์ */}
                {data.usefulness.reason_no_benefit && (
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderLeft: '4px solid #f44336',
                      bgcolor: '#fafafa'
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                          <Box sx={{ 
                            bgcolor: '#f44336',
                            color: 'white',
                            p: 1,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 40,
                            height: 40
                          }}>
                            <DescriptionIcon />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                              เหตุผลที่ผลการปฏิบัติงาน/โครงงานที่นักศึกษาสหกิจศึกษาทำไม่มีประโยชน์ต่อสถานประกอบการ
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              mt: 0.5,
                              lineHeight: 1.6,
                              color: '#d32f2f',
                              fontWeight: 'medium'
                            }}>
                              {data.usefulness.reason_no_benefit}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyledCard>
        </Box>
      )}
    </Container>
  );
};

// Main Page Component
const SupervisorEvaluationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reqIdFromUrl = searchParams.get('req_id');
  const [reqId, setReqId] = useState(reqIdFromUrl || '');
  const [showResults, setShowResults] = useState(!!reqIdFromUrl);

  useEffect(() => {
    if (reqIdFromUrl) {
      setReqId(reqIdFromUrl);
      setShowResults(true);
    }
  }, [reqIdFromUrl]);

  const handleSearch = () => {
    if (reqId.trim()) {
      // อัปเดต URL โดยใช้ relative path
      navigate(`?req_id=${reqId.trim()}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Search Section */}
        <StyledCard sx={{ mb: 4 }} className="no-print">
          <CardContent>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              🔍 ค้นหาผลการประเมินจากพี่เลี้ยง
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Supervisor Evaluation Visualization
            </Typography>
            <Box display="flex" gap={2} alignItems="end" mt={2}>
              <TextField
                fullWidth
                label="REQ ID"
                value={reqId}
                onChange={(e) => setReqId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="กรอก REQ ID เช่น 6405021601"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ minWidth: '120px', height: '56px' }}
              >
                ค้นหา
              </Button>
            </Box>
          </CardContent>
        </StyledCard>

        {/* Results */}
        {showResults && reqId && (
          <SupervisorEvaluationVisualization reqId={reqId} />
        )}
      </Container>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .MuiContainer-root { max-width: 100% !important; }
        }
      `}</style>
    </Box>
  );
};

export default SupervisorEvaluationPage;
